import { ChangeDetectorRef, Component, inject, model, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { take } from 'rxjs/operators';
import { UserOperation } from '../../../../core/enums/user-operation';
import { AbsencePlannerEventModel } from '../../../../core/models/absence-planner/absence-planner-event.model';
import { TimeBlockAbsenceModel } from '../../../../core/models/timeblock/time-block.model';
import { ShiveDialogService } from '../../../../core/services/controls/shive-dialog.service';
import { TimeBlockItemBuilderService } from '../../../calendar/components/time-block/generation/time-block-item-builder.service';
import { DialogHttpRequestHandlerService } from '../../../calendar/components/time-block/http/dialog-http-request-handler.service';
import { TimeBlockComponent } from '../../../calendar/components/time-block/time-block.component';
import { ITimeBlockComponentItem } from '../../../calendar/components/time-block/types/time-block-component-item-types';
import {
  TimeBlockContentType,
  TimeBlockDialogItem,
} from '../../../calendar/components/time-block/types/time-block-types';
import { AbsencePlannerService } from '../../services/absence-planner.service';

@Component({
  selector: 'app-planner-event-dialog',
  templateUrl: './absence-planner-event-dialog.component.html',
  standalone: false,
})
export class AbsencePlannerEventDialogComponent {
  // Inputs
  readonly plannerEventModel = model<AbsencePlannerEventModel>();
  readonly dialogForm = model<FormGroup>();
  readonly fixIntervalDropdownVisible = model(false);

  // Signals
  public readonly selectedTimeBlockComponent = signal<ITimeBlockComponentItem>(null);
  public readonly workTimeStart = signal<Date>(null);
  public readonly workTimeEnd = signal<Date>(null);

  // Constants
  public readonly UserOperation = UserOperation;
  public readonly cd = inject(ChangeDetectorRef);
  private readonly timeBlockItemBuilderService = inject(TimeBlockItemBuilderService);
  private readonly shiveDialogService = inject(ShiveDialogService);
  private readonly dialogHttpRequestHandlerService = inject(DialogHttpRequestHandlerService);
  private readonly absencePlannerService = inject(AbsencePlannerService);

  constructor() {
    toObservable(this.plannerEventModel)
      .pipe(take(1))
      .subscribe((plannerEventModel) => {
        // Set isActive to true to prevent subsequent end time transformation in TimeBlockItemBuilder service.
        const timeBlockModel = new TimeBlockAbsenceModel<TimeBlockComponent>();
        Object.assign(timeBlockModel, plannerEventModel);
        const timeBlock = this.timeBlockItemBuilderService.buildFromExisting(timeBlockModel);
        this.selectedTimeBlockComponent.set(timeBlock);
      });
    this.workTimeStart.set(this.absencePlannerService.plannerModel.workTimeStart);
    this.workTimeEnd.set(this.absencePlannerService.plannerModel.workTimeEnd);
  }

  public sendAction(operation: UserOperation): void {
    const timeBlockComponent = this.selectedTimeBlockComponent();
    timeBlockComponent.timeBlockModel.fixInterval = null;

    if (this.fixIntervalDropdownVisible()) {
      timeBlockComponent.timeBlockModel.fixInterval =
        this.dialogForm().controls.timeOfDay.value.value;
    }

    const timeBlockDialogItem: TimeBlockDialogItem = {
      operation,
      timeBlockItem: this.selectedTimeBlockComponent(),
    };

    this.dialogHttpRequestHandlerService.init(
      this.dialogForm(),
      TimeBlockContentType.Absence,
      timeBlockDialogItem,
      this.fixIntervalDropdownVisible(),
    );
  }

  public cancel(): void {
    this.shiveDialogService.close();
  }

  /**
   * Set isActive to false to prevent subsequent time modification.
   */
  public modifyPlannerEvent(plannerEvent: ITimeBlockComponentItem): ITimeBlockComponentItem {
    plannerEvent.timeBlockModel.isActive = false;
    return plannerEvent;
  }
}
